'use client';

import type { BadgeProps } from '@magicschool/spellbook/components/Badge';
import { Badge } from '@magicschool/spellbook/components/Badge';
import { Breadcrumb, type BreadcrumbProps } from '@magicschool/spellbook/components/Breadcrumb';
import { IconButton, type IconButtonProps } from '@magicschool/spellbook/components/IconButton';
import type { BreadcrumbData } from '@magicschool/spellbook/components/PageHeader';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import {
  getPanelContainerWidth,
  MAIN_CONTENT_MIN_WIDTH,
  PANEL_MIN_WIDTH,
  usePanelResize,
} from '@magicschool/spellbook/hooks/usePanelResize';
import { focusFirstElementIn } from '@magicschool/spellbook/lib/focus';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import { useEffect, useRef } from 'react';
import {
  usePageContainerContext,
  usePageContainerLayout,
  usePageContainerPanel,
  usePageContainerPanelId,
  usePageContainerText,
} from './PageContainerProvider';

/**
 * Aurora gradient decoration. Exported so surfaces that render chat outside PageContainer
 * (ChatFrame, the room-builder FakeChatPreview) share the exact recipe instead of copying
 * the class string — a design tweak here reaches every chat surface.
 */
export const pageContainerBackgroundVariants = cva(
  'bg-gradient-aurora blur-3xl absolute h-1/5 left-0 w-full right-0 pointer-events-none z-0',
  {
    variants: {
      variant: {
        'aurora-top': 'top-0',
        'aurora-bottom': 'bottom-0',
      },
    },
    defaultVariants: {
      variant: 'aurora-top',
    },
  },
);

const panelVariants = cva('z-drawer h-full relative flex flex-col', {
  variants: {
    open: {
      true: 'w-[100cqw] max-w-[100cqw] @2xl/page:max-w-[calc(100cqw-24rem)]',
      false: 'w-0',
    },
    resizing: {
      true: 'select-none',
      false: '',
    },
  },
  defaultVariants: {
    open: false,
    resizing: false,
  },
});

const resizeHandleLineVariants = cva(
  [
    'h-full',
    'w-px',
    'bg-border-default',
    'transition-colors',
    'group-hover/resize-handle:w-[2px]',
    'group-hover/resize-handle:bg-selected-default-stroke',
    'group-hover/resize-handle:delay-75',
    'group-focus/resize-handle:w-[2px]',
    'group-focus/resize-handle:bg-selected-default-stroke',
  ],
  {
    variants: {
      resizing: {
        true: 'bg-selected-default-stroke w-[2px]',
        false: '',
      },
    },
    defaultVariants: { resizing: false },
  },
);

/** Page title slot. Renders as h1 with heading-2 variant by default. */
export const PageContainerTitleSlot = Slot<TextProps>({
  displayName: 'PageContainer.Title',
  renderDefault: (props) => <Text data-spellbook-name="page-container-title" element="h1" variant="heading-2" tabIndex={-1} {...props} />,
});

/** Optional badge next to the page title. Renders as small primary badge by default. */
export const PageContainerTitleBadgeSlot = Slot<BadgeProps>({
  displayName: 'PageContainer.TitleBadge',
  renderDefault: (props) => <Badge data-spellbook-name="page-container-title-badge" variant="primary" {...props} />,
});

/** Optional page description text below the title. Renders as body-2 variant by default. */
export const PageContainerDescriptionSlot = Slot<TextProps>({
  displayName: 'PageContainer.Description',
  renderDefault: ({ className, ...props }) => (
    <Text data-spellbook-name="page-container-description" variant="body-2" className={cn('max-w-xl', className)} {...props} />
  ),
});

/** Optional decorative background gradient. Supports aurora-top (default) or aurora-bottom variants. Pages may pass overrideDefault to supply fully custom background content. */
export const PageContainerBackgroundSlot = Slot<{ variant?: 'aurora-top' | 'aurora-bottom' } & React.ComponentProps<'div'>>({
  displayName: 'PageContainer.Background',
  renderDefault: ({ variant = 'aurora-top', className, ...props }) => (
    <div
      data-spellbook-name="page-container-background"
      aria-hidden="true"
      className={cn(pageContainerBackgroundVariants({ variant }), className)}
      {...props}
    />
  ),
});

/** Heading-aligned action buttons or controls. Not rendered by default. */
export const PageContainerActionsSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.Actions',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    if (!children) return null;
    return (
      <div
        data-spellbook-name="page-container-actions"
        className={cn('flex flex-wrap shrink-0 items-center md:justify-end gap-2 max-w-full h-fit', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

/** Reserved space for navigation rendered above the sticky header and page heading. Not rendered by default. */
export const PageContainerTopNavSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.TopNav',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    if (!children) return null;
    return (
      <div data-spellbook-name="page-container-top-nav" className={cn('w-full shrink-0', className)} {...props}>
        {children}
      </div>
    );
  },
});

/** Header action buttons or controls. Right-aligned in the header area. Not rendered by default. */
export const PageContainerTopActionsSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.TopActions',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    if (!children) return null;
    return (
      <div
        data-spellbook-name="page-container-top-actions"
        className={cn('flex flex-wrap shrink-0 items-center md:justify-end gap-2 max-w-full h-fit', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

/** Main scrollable content area. Required slot - always include in JSX. */
export const PageContainerContentSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.Content',
  renderDefault: ({ children, className, ...props }) => {
    const layout = usePageContainerLayout();
    if (!children) return null;
    return (
      <div
        data-spellbook-name="page-container-content"
        className={cn('w-full flex flex-col', layout === 'fixed-height' ? 'flex-1 min-h-0 gap-0' : 'gap-6', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

/** Breadcrumb navigation. Auto-rendered when breadcrumbs prop is provided. Renders at all screen sizes. */
export const PageContainerBreadcrumbSlot = Slot<BreadcrumbProps & { breadcrumbs: BreadcrumbData[] }>({
  displayName: 'PageContainer.Breadcrumb',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({ breadcrumbs, className, ...props }) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return (
      <Breadcrumb data-spellbook-name="page-container-breadcrumb" className={cn('flex flex-1 min-w-0', className)} {...props}>
        {breadcrumbs.map(({ element: Comp, label, href, type }, i) => {
          const key = href || label || i;
          const LinkContent = Comp ? (
            <Comp href={href} key={key}>
              {label}
            </Comp>
          ) : (
            label
          );
          return type === 'link' ? (
            <Breadcrumb.Link key={key} asChild={Boolean(Comp)} href={href}>
              {LinkContent}
            </Breadcrumb.Link>
          ) : (
            <Breadcrumb.Page key={key}>{label}</Breadcrumb.Page>
          );
        })}
      </Breadcrumb>
    );
  },
});

/** Optional alert or notification area above main content. Not rendered by default. */
export const PageContainerAlertSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.Alert',
  renderDefault: ({ children, className, ...props }) => {
    if (!children) return null;

    // `empty:hidden` hides the wrapper when children render nothing (e.g. a self-gating banner that returns null),
    // so callers can mount the slot unconditionally without leaving a visible empty gap.
    return (
      <div data-spellbook-name="page-container-alert" className={cn('empty:hidden w-full flex flex-col gap-2', className)} {...props}>
        {children}
      </div>
    );
  },
});

/** Optional bottom-anchored content area. Not rendered by default. */
export const PageContainerBottomSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'PageContainer.Bottom',
  renderDefault: ({ children, className, ...props }) => {
    if (!children) return null;
    return (
      <div
        data-spellbook-name="page-container-bottom"
        className={cn('shrink-0 flex w-full relative items-center justify-end gap-2', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

/** Optional right-side panel with resize support. Slides in from right when isOpen is true. Supports Escape key to close. */
export const PageContainerPanelSlot = Slot<
  React.ComponentProps<'div'> & {
    /** Whether the panel is resizable by dragging the left edge. Default: true. */
    resizable?: boolean;
    /** Whether the panel is visible. Default: true. */
    isOpen?: boolean;
    /** Callback when panel open state changes. */
    onOpenChange?: (open: boolean) => void;
  }
>({
  displayName: 'PageContainer.Panel',
  preventOverride: true,
  renderDefault: ({
    children,
    className,
    resizable = true,
    isOpen: isOpenProp,
    onOpenChange: onOpenChangeProp,
    'aria-label': ariaLabel,
    id,
    ...props
  }) => {
    const { panelLabel, resizePanelLabel } = usePageContainerText();
    // When isOpen is not passed, the panel is uncontrolled: read state from context (managed by PageContainerBase).
    // This lets PanelOpen in Actions and PanelClose inside the panel share state without external useState.
    // The aside uses the context panelId so PanelClose/PanelToggle can reference it via aria-controls.
    // If multiple Panel siblings are rendered, pass a distinct `id` prop on each to avoid duplicate DOM ids.
    const { panel: panelCtx, isOverlayPanel, panelId } = usePageContainerContext();
    const isOpen = isOpenProp ?? panelCtx?.isOpen ?? true;
    const onOpenChange = isOpenProp !== undefined ? onOpenChangeProp : panelCtx?.onOpenChange;
    const panelRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    // null = not yet mounted; thereafter tracks the previous isOpen value.
    // Comparing against the previous value (rather than a simple isMounted flag)
    // makes this safe under React Strict Mode, which double-invokes effects on
    // mount: the second invocation sees prevIsOpen === isOpen and skips both branches.
    const prevIsOpenRef = useRef<boolean | null>(null);
    const { panelWidth, isResizing, handleMouseDown, handleResizeKeyDown } = usePanelResize(panelRef, resizable, isOpen);

    // Move focus into the panel on open; return focus to the page container on close.
    // Only fires on genuine open/close transitions, never on initial mount.
    useEffect(() => {
      const prevIsOpen = prevIsOpenRef.current;
      prevIsOpenRef.current = isOpen;

      if (prevIsOpen === null) return; // initial mount — skip
      if (isOpen && !prevIsOpen) {
        focusFirstElementIn(innerRef.current);
      } else if (!isOpen && prevIsOpen) {
        // Focus the nearest focusable ancestor of the panel — falls back gracefully
        // if the host page does not expose a specific skip-to target.
        (panelRef.current?.closest('[data-spellbook-name="page-container"]') as HTMLElement | null)?.focus();
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen || !onOpenChange) return;
      const handleEscape = (e: KeyboardEvent) => {
        // Respect e.defaultPrevented so nested Radix dropdowns, modals, or
        // comboboxes that handle Escape first don't also close the panel.
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.preventDefault();
          onOpenChange?.(false);
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onOpenChange]);

    return (
      <aside
        id={id ?? panelId}
        ref={panelRef}
        data-spellbook-name="page-container-panel"
        aria-label={ariaLabel ?? panelLabel}
        aria-modal={isOverlayPanel ? 'true' : undefined}
        className={cn(panelVariants({ open: isOpen, resizing: isResizing }), '@container/panel', className)}
        style={isOpen && panelWidth ? { width: panelWidth } : undefined}
        {...props}
        inert={!isOpen || undefined}
      >
        {isOpen && (
          // biome-ignore lint/a11y/useSemanticElements: role="separator" with tabIndex is intentional for an interactive panel resize handle — <hr> is non-interactive and cannot serve this purpose
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={resizePanelLabel}
            aria-valuenow={panelWidth || panelRef.current?.offsetWidth || 0}
            aria-valuemin={PANEL_MIN_WIDTH}
            aria-valuemax={getPanelContainerWidth(panelRef) - MAIN_CONTENT_MIN_WIDTH}
            tabIndex={0}
            className="absolute start-0 top-0 h-full w-2 cursor-col-resize z-drawer group/resize-handle hidden @2xl/page:block"
            onMouseDown={handleMouseDown}
            onKeyDown={handleResizeKeyDown}
          >
            <div className={resizeHandleLineVariants({ resizing: isResizing })} />
          </div>
        )}

        <div ref={innerRef} tabIndex={-1} className="overflow-y-auto w-full h-full outline-none">
          {children}
        </div>
      </aside>
    );
  },
});

/** Button that closes the panel. Use `asChild` to render your own trigger. */
export const PageContainerPanelCloseSlot = Slot<IconButtonProps>({
  displayName: 'PageContainer.PanelClose',
  preventOverride: true,
  renderDefault: ({ onClick, ...props }) => {
    const { onOpenChange } = usePageContainerPanel();
    const { closePanelLabel } = usePageContainerText();
    const panelId = usePageContainerPanelId();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onOpenChange?.(false);
      onClick?.(e);
    };

    return (
      <IconButton
        data-spellbook-name="page-container-panel-close"
        name="x"
        variant="ghost"
        color="neutral"
        label={closePanelLabel}
        aria-controls={panelId || undefined}
        {...props}
        onClick={handleClick}
      />
    );
  },
});

/** Button that toggles the panel open and closed. Use `asChild` to render your own trigger. */
export const PageContainerPanelToggleSlot = Slot<IconButtonProps>({
  displayName: 'PageContainer.PanelToggle',
  preventOverride: true,
  renderDefault: ({ onClick, ...props }) => {
    const { isOpen, onOpenChange } = usePageContainerPanel();
    const { togglePanelLabel } = usePageContainerText();
    const panelId = usePageContainerPanelId();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onOpenChange?.(!isOpen);
      onClick?.(e);
    };

    return (
      <IconButton
        data-spellbook-name="page-container-panel-toggle"
        name={isOpen ? 'panel-right-close' : 'panel-right'}
        variant="ghost"
        color="neutral"
        label={togglePanelLabel}
        aria-expanded={isOpen}
        aria-controls={panelId || undefined}
        {...props}
        className={cn('rtl:[&_[data-spellbook-name=icon]]:-scale-x-100', props.className)}
        onClick={handleClick}
      />
    );
  },
});
